import { useEffect, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { PlusIcon } from "@heroicons/react/24/outline";
import { cn } from "@utils";
import { WORKING_SET_COLUMNS, moveToSlot, placeWorkingSets } from "../hooks/workingSets";
import { useFlipLayout } from "../hooks/useFlipLayout";
import WorkingSetCard, { CARD, WorkingSetCardOverlay } from "./WorkingSetCard";

// Columns follow the space the board actually has (both sidebars can be open), not the viewport:
// each slot is at least CARD_MIN_WIDTH wide and the row never holds more than WORKING_SET_COLUMNS,
// so a narrow page wraps onto more rows instead of squashing the cards.
const CARD_MIN_WIDTH = "16rem";
const GAP = "1rem";
const BOARD_COLUMNS = {
  gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, max(${CARD_MIN_WIDTH}, calc((100% - ${WORKING_SET_COLUMNS - 1} * ${GAP}) / ${WORKING_SET_COLUMNS}))), 1fr))`,
};

const Slot = ({ index, set, dragging, prominent, children, onAdd }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `slot-${index}`, data: { slot: index } });
  const isSource = dragging && set?.id === dragging.id;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "relative rounded-2xl transition-shadow",
        isOver && !isSource && "ring-2 ring-zinc-900 ring-offset-2",
      )}
    >
      {set ? (
        children
      ) : !dragging && !prominent ? (
        // A gap between cards: holds its place on the board but stays out of sight until a drag.
        <div aria-hidden="true" className={cn(CARD, "invisible")} />
      ) : (
        <button
          type="button"
          onClick={() => onAdd(index)}
          disabled={!!dragging}
          aria-label={`Add a working set in slot ${index + 1}`}
          className={cn(
            CARD,
            "group w-full flex-row items-center justify-center gap-1.5 border-dashed text-sm transition-colors",
            dragging
              ? "border-zinc-300 bg-zinc-50/60 text-transparent"
              : "border-zinc-300 text-zinc-500 hover:border-zinc-400 hover:text-zinc-700 focus-visible:border-zinc-400 focus-visible:outline-none",
          )}
        >
          <PlusIcon aria-hidden="true" className="size-5" />
          Add working set
        </button>
      )}
    </div>
  );
};

// Home's 5×2 board. A card can be dropped into any slot: an empty one takes it, an occupied one
// swaps. At rest only the first empty slot shows (as "Add working set"); gaps between cards keep
// their place invisibly and trailing empty slots aren't rendered, so there's no blank row. A drag
// reveals every slot as a drop target. Mouse drags start after a small move so a click still
// opens the set; touch needs a short press so the page can still scroll.
const WorkingSetBoard = ({ sets, onPlace, onAdd, onEdit, onDuplicate, onRemove }) => {
  const [dragging, setDragging] = useState(null);
  const lastDragEndRef = useRef(0);
  const { containerRef, startFrom } = useFlipLayout();
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 6 } }),
    useSensor(KeyboardSensor),
  );
  // A drop is shown straight away from local state, in the same render the drag ends, so the
  // layout animation sees one move rather than a bounce back while the shared cache catches up.
  // It stands only until the stored layout changes from what it was at the drop: the save landing
  // (same layout), or a failed save refetching (the old one).
  const placed = placeWorkingSets(sets);
  const placedKey = placed.map((s) => s?.id ?? "").join("|");
  const [dropped, setDropped] = useState(null);
  useEffect(() => {
    if (dropped && placedKey !== dropped.basis) setDropped(null);
  }, [placedKey, dropped]);
  const slots =
    dropped && dropped.basis === placedKey
      ? dropped.slots.map((s) => s && (sets.find((x) => x.id === s.id) ?? s))
      : placed;
  const firstEmpty = slots.indexOf(null);
  const lastFilled = slots.reduce((last, set, i) => (set ? i : last), -1);
  const shownSlots = dragging ? slots.length : Math.max(lastFilled, firstEmpty) + 1;
  const endDrag = () => {
    setDragging(null);
    lastDragEndRef.current = Date.now();
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={({ active }) => setDragging(active.data.current.set)}
      onDragCancel={({ active }) => {
        startFrom(active.id, active.rect.current.translated);
        endDrag();
      }}
      onDragEnd={({ active, over }) => {
        const set = active.data.current.set;
        // The card lands from where it was let go (not its old slot) and anything it displaced
        // glides across; both are the layout animation, since the move is applied immediately.
        startFrom(set.id, active.rect.current.translated);
        endDrag();
        if (!over) return;
        const next = moveToSlot(slots, set.id, over.data.current.slot);
        if (next === slots) return;
        setDropped({ slots: next, basis: placedKey });
        onPlace(set, over.data.current.slot);
      }}
      accessibility={{
        announcements: {
          onDragStart: ({ active }) => `Picked up ${active.data.current.set.name}.`,
          onDragOver: ({ over }) => (over ? `Over slot ${over.data.current.slot + 1}.` : undefined),
          onDragEnd: ({ active, over }) =>
            over
              ? `Dropped ${active.data.current.set.name} in slot ${over.data.current.slot + 1}.`
              : `${active.data.current.set.name} put back.`,
          onDragCancel: ({ active }) => `${active.data.current.set.name} put back.`,
        },
      }}
    >
      <div ref={containerRef} className="grid gap-4" style={BOARD_COLUMNS}>
        {slots.slice(0, shownSlots).map((set, i) => (
          <Slot key={i} index={i} set={set} dragging={dragging} prominent={i === firstEmpty} onAdd={onAdd}>
            {set && (
              <div data-flip-id={set.id} className="relative h-full">
                <WorkingSetCard
                  key={set.id}
                  set={set}
                  onEdit={onEdit}
                  onDuplicate={onDuplicate}
                  onRemove={onRemove}
                  wasJustDragged={() => Date.now() - lastDragEndRef.current < 400}
                />
              </div>
            )}
          </Slot>
        ))}
      </div>
      <DragOverlay dropAnimation={null}>
        {dragging ? <WorkingSetCardOverlay set={sets.find((s) => s.id === dragging.id) ?? dragging} /> : null}
      </DragOverlay>
    </DndContext>
  );
};

export default WorkingSetBoard;
